import React from 'react';
import { Zap, Headphones, Lock, Award, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Reveal, SectionHeading } from './ui-blocks';

const BUILDING_IMAGE =
  'https://images.unsplash.com/photo-1769184614794-f6e910de6128?crop=entropy&cs=srgb&fm=jpg&q=80&w=1000';

const ICONS = [Zap, Headphones, Lock, Award, MapPin];

const Marquee = ({ items }) => {
  const doubled = [...items, ...items];
  return (
    <div className="mt-24 md:mt-32 bg-gold text-ink overflow-hidden py-5 border-y border-ink/10" data-testid="marquee-strip" aria-hidden="true">
      <div className="marquee-track">
        {doubled.map((item, i) => (
          <span key={i} className="flex items-center gap-8 pr-8 font-heading text-2xl md:text-3xl whitespace-nowrap">
            {item}
            <span className="w-2 h-2 rounded-full bg-bordeaux" />
          </span>
        ))}
      </div>
    </div>
  );
};

const WhyChoose = () => {
  const { t } = useLanguage();

  return (
    <section id="why-choose" data-testid="why-choose-section" className="bg-cream pt-24 md:pt-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <Reveal className="lg:sticky lg:top-28">
            <div className="relative aspect-[3/4] max-w-md">
              <div className="absolute -top-4 -left-4 md:-top-6 md:-left-6 inset-0 border border-bordeaux" />
              <img src={BUILDING_IMAGE} alt="" className="relative h-full w-full object-cover grayscale-[30%]" />
              <div className="absolute bottom-0 left-0 right-0 bg-ink/80 text-cream px-6 py-4 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-gold">BiTExpress</span>
                <span className="text-xs text-cream/70">Abidjan · Côte d'Ivoire</span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <SectionHeading eyebrow={t.whyChoose.eyebrow} title={t.whyChoose.title} subtitle={t.whyChoose.subtitle} testId="why-choose-heading" />

          <ul className="mt-14 border-t border-ink/10" data-testid="why-choose-list">
            {t.whyChoose.reasons.map((reason, index) => {
              const Icon = ICONS[index];
              return (
                <Reveal as="li" key={reason.title} delay={index * 0.06}>
                  <div
                    data-testid={`why-choose-item-${index}`}
                    className="group flex items-center gap-6 md:gap-8 py-6 md:py-7 border-b border-ink/10 hover:pl-3 transition-[padding] duration-300"
                  >
                    <span className="shrink-0 w-11 h-11 flex items-center justify-center bg-bordeaux text-cream">
                      <Icon className="w-5 h-5" strokeWidth={1.5} />
                    </span>
                    <div className="flex-1 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-6">
                      <h3 className="font-heading text-xl sm:text-2xl text-ink">{reason.title}</h3>
                      <p className="text-sm md:text-base text-ink/55 sm:text-right">{reason.desc}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </ul>
        </div>
      </div>

      <Marquee items={t.whyChoose.marquee} />
    </section>
  );
};

export default WhyChoose;
